import {
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  collection,
  addDoc,
  onSnapshot,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase";
import { matrixToString, validateMatrix } from "../components/Risk Matrix/riskMatrixShared";

const riskMatrixService = {
  // Risk Matrix specific methods
  async createRiskMatrix(matrixData) {
    try {
      // Validate required fields
      if (!validateMatrix(matrixData)) {
        throw new Error("Invalid matrix data format");
      }

      // Convert matrix to string format if it's an array
      const formattedMatrix = matrixToString(matrixData.matrix);

      // Prepare data for saving
      const matrixToSave = {
        name: matrixData.name,
        probabilities: matrixData.probabilities,
        severities: matrixData.severities,
        matrix: formattedMatrix,
        colors: matrixData.colors || {},
        descriptions: matrixData.descriptions || {
          probabilities: {},
          severities: {},
        },
        diagonalText: matrixData.diagonalText || {
          top: "Severity",
          bottom: "Probability",
        },
        isAxesFlipped: matrixData.isAxesFlipped || false, // Preserve the axes orientation
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Create a specific collection reference for risk matrices
      const matricesCollectionRef = collection(db, "riskHelper");

      // Add the document
      const docRef = await addDoc(matricesCollectionRef, matrixToSave);
      console.log("Risk matrix created with ID:", docRef.id);

      return docRef.id;
    } catch (error) {
      console.error("Error creating risk matrix:", error);
      throw error;
    }
  },

  async getNewMatrices() {
    try {
      const matricesRef = collection(db, "riskHelper");
      const querySnapshot = await getDocs(matricesRef);

      // Map the documents to a more usable format
      const matrices = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          probabilities: data.probabilities,
          severities: data.severities,
          matrix: data.matrix, // Let the shared function handle conversion
          colors: data.colors,
          descriptions: data.descriptions || {},
          diagonalText: data.diagonalText || {},
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        };
      });

      return matrices;
    } catch (error) {
      console.error("Error fetching new matrices:", error);
      throw error;
    }
  },

  async updateRiskMatrix(matrixId, matrixData) {
    try {
      const docRef = doc(db, "riskHelper", matrixId);

      // Convert matrix array to string format for storage
      const updatedMatrix = {
        ...matrixData,
        matrix: matrixToString(matrixData.matrix),
        updatedAt: serverTimestamp(),
      };

      await updateDoc(docRef, updatedMatrix);
      console.log("Risk matrix updated successfully.");
    } catch (error) {
      console.error("Error updating risk matrix: ", error);
      throw error;
    }
  },

  async deleteRiskMatrix(matrixId) {
    try {
      // First check if matrix is being used by any projects
      const projectsRef = collection(db, "projects");
      const q = query(projectsRef, where("riskMatrix", "==", matrixId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        throw new Error(
          "Cannot delete matrix as it is being used by existing projects"
        );
      }

      await deleteDoc(doc(db, "riskHelper", matrixId));
      console.log("Risk matrix deleted successfully.");
    } catch (error) {
      console.error("Error deleting risk matrix: ", error);
      throw error;
    }
  },

  async getAllriskHelper() {
    try {
      const querySnapshot = await getDocs(collection(db, "riskHelper"));

      const matrices = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        // Don't convert here, let shared functions handle it
        // when needed by components
      }));

      return matrices;
    } catch (error) {
      console.error("Error fetching risk matrices: ", error);
      throw error;
    }
  },

  // Subscribe to changes in risk matrices
  subscribeToriskHelper(callback) {
    const matricesRef = collection(db, "riskHelper");

    return onSnapshot(matricesRef, (snapshot) => {
      const matrices = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        // Don't convert here, let shared functions handle it
        // when needed by components
      }));

      callback(matrices);
    });
  },

  // Get all matrices accessible to a user
  async getAccessibleriskHelper(userId) {
    try {
      // Get user's role from their document
      const userDoc = await this.get("users", userId);
      const isAdmin = userDoc?.role === "admin";
      const isManager = userDoc?.role === "manager";

      // Get all matrices
      const matrices = await this.getAllriskHelper();

      // Filter matrices based on user's role and permissions
      // Admins and managers see all matrices, others see only public ones
      return matrices.filter(
        (matrix) => isAdmin || isManager || !matrix.isPrivate
      );
    } catch (error) {
      console.error("Error fetching accessible risk matrices: ", error);
      throw error;
    }
  },

  // Helper method to validate matrix format
  validateMatrixData(matrixData) {
    // Validate using the shared function
    const isValid = validateMatrix(matrixData);
    
    if (isValid) {
      return { isValid: true, errors: [] };
    }
    
    // If validation failed, provide more specific errors
    const errors = [];
    
    // Check required fields
    if (!matrixData.name) errors.push("Missing matrix name");
    if (!matrixData.probabilities || !matrixData.probabilities.length) 
      errors.push("Missing probabilities");
    if (!matrixData.severities || !matrixData.severities.length) 
      errors.push("Missing severities");
    if (!matrixData.matrix) 
      errors.push("Missing matrix data");
    if (!matrixData.colors) 
      errors.push("Missing color definitions");
    
    // If we have the basic fields but validation still failed,
    // likely a dimensional issue or missing colors
    if (errors.length === 0) {
      // Check matrix dimensions
      const matrix = Array.isArray(matrixData.matrix)
        ? matrixData.matrix
        : matrixData.matrix.split(";").map((row) => row.split("|"));
        
      if (matrix.length !== matrixData.probabilities.length) {
        errors.push("Matrix rows do not match number of probabilities");
      } else if (matrix.some(row => row.length !== matrixData.severities.length)) {
        errors.push("Matrix columns do not match number of severities");
      } else {
        // Check for missing colors
        const uniqueRatings = new Set();
        matrix.forEach(row => row.forEach(cell => {
          if (cell) uniqueRatings.add(cell);
        }));
        
        const missingColors = Array.from(uniqueRatings).filter(
          rating => !matrixData.colors[rating]
        );
        
        if (missingColors.length > 0) {
          errors.push(`Missing colors for ratings: ${missingColors.join(', ')}`);
        }
      }
    }
    
    return { isValid: false, errors: errors.length > 0 ? errors : ["Invalid matrix format"] };
  },
};

export default riskMatrixService;
